import { Row } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import ViewProductLogSheet from "../ViewProductLogSheet";

import { useAwaitingRequestColumns } from "./AwaitingRequestColumns";

import FetchWrapper from "@/components/reusables/FetchWrapper";
import CustomHeader from "@/components/reusables/Headers.tsx";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants } from "@/constants/tools.constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import { useGetParams } from "@/hooks/useGetParams";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import {
  IProductAssignment,
  IProductLogsItem,
  ProjectAssignToolsStatus,
} from "@/interfaces/tools.interface";
import { useGetAllProductAssignmentQuery } from "@/redux/api/projectsQuery";

const ViewAwaitingRequestProducts = (): JSX.Element => {
  const { PRODUCTS } = ToolsConstants;
  const navigate = useNavigate();
  const { id } = useParams();
  const [productLog, setProductLog] = React.useState<{
    isOpen: boolean;
    itemName: string;
    data: IProductLogsItem[];
  } | null>(null);

  const filters = useGetParams(["search"]);

  const { data: products, ...rest } = useGetAllProductAssignmentQuery({
    projectId: id!,
    filters,
    status: ProjectAssignToolsStatus.AWAITING,
  });

  const getActionOptions = (
    row: Row<IProductAssignment>,
  ): ActionButtonDataTypes[] => {
    const defaultActions: ActionButtonDataTypes[] = [
      {
        icon: FileText,
        name: "View Logs",
        onclick: (): void => {
          setProductLog({
            isOpen: true,
            itemName: row.original?.product?.name || "",
            data: row.original?.product.logs || [],
          });
        },
      },
    ];

    return defaultActions;
  };

  const columns = useAwaitingRequestColumns({
    getActionOptions,
  });

  const handleBack = (): void => {
    navigate(ROUTES.TOOLS + ROUTES.REQUEST);
  };

  return (
    <DataTableSlotContextProvider>
      <CustomHeader title={PRODUCTS.HEADER} backBtn={handleBack} />
      <FetchWrapper {...rest} isTable>
        <CustomTable
          title={PRODUCTS.HEADER}
          columns={columns}
          data={products?.data ?? []}
          empty={{}}
          hide={{
            filters: true,
            functions: true,
          }}
          functions={{
            search: PRODUCTS.HEADER,
            isExport: true,
            columnToggle: true,
            RightClickData: (
              row: GlobalRowDataTypes<IProductAssignment>,
            ): ActionButtonDataTypes[] =>
              getActionOptions(row as Row<IProductAssignment>),
          }}
        />
      </FetchWrapper>
      {productLog?.isOpen && (
        <ViewProductLogSheet
          toggleProductLog={() => setProductLog(null)}
          {...productLog}
        />
      )}
    </DataTableSlotContextProvider>
  );
};

export default ViewAwaitingRequestProducts;
